import cx from 'classnames';
import { CSSProperties, FC, FocusEvent, InputHTMLAttributes, KeyboardEvent, MouseEvent } from 'react';
import CrossIcon from '@/storybook/icons/CrossIcon';
import SearchIcon from '@/storybook/icons/SearchIcon';
import ScrollArea from '../ScrollArea/ScrollArea';
import { SEARCH_SIZE, SEARCH_STATE, SEARCH_VARIANT, SearchSize, SearchState, SearchVariant } from './Search.constants';
import { useSearchController } from './useSearchController';
import styles from './Search.module.css';

const DEFAULT_PLACEHOLDER = 'Search...';
const DEFAULT_ARIA_LABEL = 'Search';
const DEFAULT_CLEAR_ARIA_LABEL = 'Clear search';
const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_TEST_ID = 'search';
const DEFAULT_INPUT_ROLE = 'searchbox';
const SUGGESTIONS_MAX_HEIGHT = '20rem';

export { SEARCH_SIZE, SEARCH_STATE, SEARCH_VARIANT };
export type SearchInputRole = 'searchbox' | 'textbox';
export type { SearchSize, SearchState, SearchVariant };

interface SearchElementTestIds {
  icon?: string;
  input?: string;
  clearButton?: string;
}

type SearchNativeInputProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  | 'aria-activedescendant'
  | 'aria-autocomplete'
  | 'aria-controls'
  | 'aria-expanded'
  | 'className'
  | 'defaultValue'
  | 'onChange'
  | 'role'
  | 'size'
  | 'type'
  | 'value'
>;

export interface SearchProps extends SearchNativeInputProps {
  'size'?: SearchSize;
  'variant'?: SearchVariant;
  'state'?: SearchState;
  'value'?: string;
  'width'?: CSSProperties['width'];
  'maxWidth'?: CSSProperties['maxWidth'];
  'inputRole'?: SearchInputRole;
  'debounce'?: number;
  'onChange'?: (value: string) => void;
  'onClear'?: () => void;
  'clearAriaLabel'?: string;
  'className'?: string;
  'data-testid'?: string;
  'elementTestIds'?: SearchElementTestIds;
  'aria-label'?: string;
  'autoSuggestions'?: string[];
  'onSuggestionSelect'?: (suggestion: string) => void;
}

interface SearchSuggestionsProps {
  dataTestId: string;
  filteredSuggestions: string[];
  activeSuggestionIndex: number;
  onSuggestionSelect: (suggestion: string) => void;
}

const SearchSuggestions: FC<SearchSuggestionsProps> = ({
  dataTestId,
  filteredSuggestions,
  activeSuggestionIndex,
  onSuggestionSelect,
}) => {
  const handleSuggestionMouseDown = (event: MouseEvent<HTMLLIElement>) => {
    event.preventDefault();
  };

  return (
    <ScrollArea.Autosize
      className={styles.suggestionsScrollArea}
      classNames={{ viewport: styles.suggestionsViewport }}
      maxHeight={SUGGESTIONS_MAX_HEIGHT}
      scrollbars="y"
      overscrollBehavior="contain"
      data-testid={`${dataTestId}-suggestions-scroll-area`}
    >
      <ul
        className={styles.suggestions}
        role="listbox"
        id={`${dataTestId}-suggestions`}
        data-testid={`${dataTestId}-suggestions`}
      >
        {filteredSuggestions.map((suggestion, index) => (
          <li
            key={`${suggestion}-${index}`}
            className={styles.suggestionItem}
            role="option"
            id={`${dataTestId}-suggestion-${index}`}
            aria-selected={index === activeSuggestionIndex}
            data-active={index === activeSuggestionIndex}
            onMouseDown={handleSuggestionMouseDown}
            onClick={() => onSuggestionSelect(suggestion)}
            data-testid={`${dataTestId}-suggestion-${index}`}
          >
            {suggestion}
          </li>
        ))}
      </ul>
    </ScrollArea.Autosize>
  );
};

const Search: FC<SearchProps> = ({
  size = SEARCH_SIZE.M,
  variant = SEARCH_VARIANT.BASIC,
  state,
  placeholder = DEFAULT_PLACEHOLDER,
  value = '',
  disabled = false,
  width,
  maxWidth,
  autoFocus = false,
  inputRole = DEFAULT_INPUT_ROLE,
  debounce = DEFAULT_DEBOUNCE_MS,
  onChange,
  onClear,
  clearAriaLabel = DEFAULT_CLEAR_ARIA_LABEL,
  className,
  'data-testid': dataTestId = DEFAULT_TEST_ID,
  elementTestIds,
  'aria-label': ariaLabel = DEFAULT_ARIA_LABEL,
  autoSuggestions = [],
  onSuggestionSelect,
  onBlur,
  onFocus,
  onKeyDown,
  ...inputProps
}) => {
  const {
    activeSuggestionIndex,
    filteredSuggestions,
    handleBlur,
    handleChange,
    handleClear,
    handleFocus,
    handleKeyDown,
    handleSuggestionClick,
    inputRef,
    internalValue,
    isDisabled,
    isFilled,
    isFocusedVisible,
    isHoveredVisible,
    isSuggestionsVisible,
  } = useSearchController({
    autoSuggestions,
    debounce,
    disabled,
    onChange,
    onClear,
    onSuggestionSelect,
    state,
    value,
  });

  const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    handleBlur();
    onBlur?.(event);
  };

  const handleInputFocus = (event: FocusEvent<HTMLInputElement>) => {
    handleFocus();
    onFocus?.(event);
  };

  const handleInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyDown?.(event);

    if (!event.defaultPrevented) {
      handleKeyDown(event);
    }
  };

  const handleWrapperClick = (event: MouseEvent<HTMLDivElement>) => {
    const eventTarget = event.target;

    if (!(eventTarget instanceof HTMLElement)) {
      return;
    }

    if (eventTarget.closest('input,button')) {
      return;
    }

    inputRef.current?.focus();
  };

  const resolvedElementTestIds = {
    icon: elementTestIds?.icon ?? `${dataTestId}-icon`,
    input: elementTestIds?.input ?? `${dataTestId}-input`,
    clearButton: elementTestIds?.clearButton ?? `${dataTestId}-clear`,
  };

  return (
    <div
      className={cx(styles.searchContainer, className)}
      style={{ width, maxWidth }}
      data-testid={dataTestId}
      data-size={size}
      data-variant={variant}
      data-disabled={isDisabled}
      data-focused={isFocusedVisible}
      data-hovered={isHoveredVisible}
      data-filled={isFilled}
    >
      <div className={styles.inputWrapper} onClick={handleWrapperClick}>
        <SearchIcon className={styles.searchIcon} data-testid={resolvedElementTestIds.icon} aria-hidden="true" />
        <input
          {...inputProps}
          ref={inputRef}
          className={styles.input}
          type="text"
          name={inputProps.name ?? resolvedElementTestIds.input}
          placeholder={placeholder}
          value={internalValue}
          autoFocus={autoFocus}
          onChange={handleChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleInputKeyDown}
          disabled={isDisabled}
          aria-label={ariaLabel}
          role={inputRole}
          aria-autocomplete={autoSuggestions.length > 0 ? 'list' : undefined}
          aria-controls={isSuggestionsVisible ? `${dataTestId}-suggestions` : undefined}
          aria-expanded={isSuggestionsVisible}
          aria-activedescendant={
            activeSuggestionIndex >= 0 ? `${dataTestId}-suggestion-${activeSuggestionIndex}` : undefined
          }
          data-testid={resolvedElementTestIds.input}
        />
        {isFilled && !isDisabled && (
          <button
            className={styles.clearButton}
            onClick={handleClear}
            type="button"
            aria-label={clearAriaLabel}
            data-testid={resolvedElementTestIds.clearButton}
          >
            <CrossIcon className={styles.clearIcon} aria-hidden="true" />
          </button>
        )}
      </div>
      {isSuggestionsVisible && (
        <SearchSuggestions
          dataTestId={dataTestId}
          filteredSuggestions={filteredSuggestions}
          activeSuggestionIndex={activeSuggestionIndex}
          onSuggestionSelect={handleSuggestionClick}
        />
      )}
    </div>
  );
};

export default Search;
